import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {from, of} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';

import {LoadError} from '@app/core/store/actions/error.actions';
import {
	AddCard,
	AddCardSuccess,
	ESubscriptionActions,
	GetCard,
	GetCardSuccess,
	GetHostedInvoice,
	GetInvoices,
	GetInvoicesSuccess,
	GetProducts,
	GetProductsSuccess,
	GetSubscriptions,
	GetSubscriptionsSuccess,
	SubscribeToPlan,
	SubscribeToPlanSuccess,
	UnsubscribeToPlan,
	UnsubscribeToPlanSuccess,
} from '@app/core/store/actions/subscription.actions';
import {SubscriptionService} from '@app/core/services/api/subscription.service';
import {DepartmentSubscription, Invoice, Product} from '@app/core/models/subscription.model';
import {Card} from '@app/core/models/card.model';
import {PaginationHolder} from '@app/core/models/pagination.model';
import {IHostedInvoiceLinkModel} from '@app/core/models/hosted-invoice-link.model';

@Injectable()
export class SubscriptionEffects {

	@Effect()
	public onGetSubscriptions$ = this.actions$.pipe(
		ofType<GetSubscriptions>(ESubscriptionActions.GetSubscriptions),
		switchMap((action: GetSubscriptions) => this.subscriptionService.getSubscriptions(action.department_id).pipe(
			switchMap((subscriptions: PaginationHolder<DepartmentSubscription>) => [
				new GetSubscriptionsSuccess(subscriptions.data),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetProducts$ = this.actions$.pipe(
		ofType<GetProducts>(ESubscriptionActions.GetProducts),
		switchMap((action: GetProducts) => this.subscriptionService.getProducts().pipe(
			switchMap((subscriptions: PaginationHolder<Product>) => [
				new GetProductsSuccess(subscriptions.data),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetInvoices$ = this.actions$.pipe(
		ofType<GetInvoices>(ESubscriptionActions.GetInvoices),
		switchMap((action: GetInvoices) => this.subscriptionService.getInvoices(action.department_id, action.page, action.pageSize).pipe(
			switchMap((res: PaginationHolder<Invoice>) => [
				new GetInvoicesSuccess(res.data, res.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onSubscribeToPlan$ = this.actions$.pipe(
		ofType<SubscribeToPlan>(ESubscriptionActions.SubscribeToPlan),
		switchMap((action: SubscribeToPlan) => this.subscriptionService.subscribeToPlan(action.department_id, action.product_id, action.plan_id).pipe(
			switchMap((subscription: DepartmentSubscription) => [
				new SubscribeToPlanSuccess(subscription)
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onUnsubscribeToPlan$ = this.actions$.pipe(
		ofType<UnsubscribeToPlan>(ESubscriptionActions.UnsubscribeToPlan),
		switchMap((action: UnsubscribeToPlan) => this.subscriptionService.unsubscribeToPlan(action.subscription).pipe(
			switchMap((subscription: DepartmentSubscription) => [
				new UnsubscribeToPlanSuccess(subscription),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetCard$ = this.actions$.pipe(
		ofType<GetCard>(ESubscriptionActions.GetCard),
		switchMap((action: GetCard) => this.subscriptionService.getCard(action.department_id).pipe(
			switchMap((cards: Card) => [
				new GetCardSuccess(cards),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onAddCard$ = this.actions$.pipe(
		ofType<AddCard>(ESubscriptionActions.AddCard),
		switchMap((action: AddCard) => this.subscriptionService.postCard(action.department_id, action.token).pipe(
			switchMap((card: Card) => [
				new AddCardSuccess(card),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetHostedInvoice$ = this.actions$.pipe(
		ofType<GetHostedInvoice>(ESubscriptionActions.GetHostedInvoice),
		switchMap((action: GetHostedInvoice) =>
			from(
				this.subscriptionService.getHostedInvoice(action.id).pipe(
					tap((link: IHostedInvoiceLinkModel) => {
						window.location.href = link.hostedPageUrl;
					}),
					catchError((error) => of(new LoadError(error, action)))
				),
			),
		),
	);

	constructor(
		private subscriptionService: SubscriptionService,
		private actions$: Actions,
	) {
	}
}
